import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import axios, { CancelToken } from "axios";

import { StatusCode } from "api/protocols";
import { GetOrder, GetItemSalesSummary } from "api/rpc/2024-04/facilityAdmin/order/order";
import { IOrder } from "redux/reducers/models/order";

import { useOrderFilters } from "hooks/useOrderFilters/useDropFilters";
import { useCustomerContext } from "../../context/CustomerContext";

import Card from "components/card/Card";
import Search from "components/search/Search";
import OrdersTable from "components/orderTable/OrdersTable";
import Spin from "components/spin/spin";
import { LocaleCurrency } from "helpers/Locale";

import { IOrderFilterState } from "pages/secure/facility/order/Orders";
import OrderFilters from "pages/secure/facility/order/OrderFilters";

import "./customerItemSales.scss";

type CustomerItemFilters = {
  search: string;
  limit: number;
  offset: number;
};

type TItemSalesSummary = {
  order_count: number;
  total_spend: number;
};

export default function CustomerItemSales() {
  const { t } = useTranslation();

  const { state } = useCustomerContext();
  const orderFilters = useOrderFilters();

  const [orders, setOrders] = useState<IOrder[]>(undefined);
  const [filters, setFilters] = useState<CustomerItemFilters>({
    search: "",
    limit: 50,
    offset: 0,
  });

  const [filterState, setFilterState] = useState<IOrderFilterState>({
    all: undefined,
    date: [],
    financial_status: [],
    register_ids: [],
    user_ids: [],
    sales_channels: [],
    credit_card: [],
    payment_type: [],
  });

  const [itemSalesSummary, setItemSalesSummary] = useState<TItemSalesSummary>(undefined);

  // Reload orders after search || offset changes.
  useEffect(() => {
    if (filterState.all === undefined) {
      return;
    }

    const source = axios.CancelToken.source();
    void loadOrders(source.token);
    void loadItemSalesSummary(source.token);

    return () => {
      source.cancel();
    };
  }, [filters, filterState.all]);

  async function loadOrders(token?: CancelToken) {
    if (orders !== undefined) {
      setOrders(undefined);
    }

    const orderRes = await GetOrder(
      {
        customer_id: state.customer.id,
        ...filters,
        filters: filterState.all?.length > 0 ? filterState.all : undefined,
      },
      false,
      token,
    );

    if (token.reason) {
      setOrders(undefined); // front-end refresh
      return;
    }

    if (orderRes.status !== StatusCode.OK) {
      setOrders([]);
    } else {
      setOrders(orderRes.data);
    }
  }

  async function loadItemSalesSummary(token?: CancelToken) {
    if (itemSalesSummary !== undefined) {
      setItemSalesSummary(undefined);
    }

    const summaryRes = await GetItemSalesSummary({ customer_id: state.customer.id }, token ? false : true, token);

    if (token && token.reason) {
      setItemSalesSummary(undefined);
      return;
    }

    if (summaryRes.status !== StatusCode.OK) {
      setItemSalesSummary({ total_spend: 0, order_count: 0 });
      return;
    }

    setItemSalesSummary(summaryRes.data[0]);
  }

  return (
    <div className="mt-2 mb-4">
      <h1 className="ui-customer_page-header ui-customer-item-sales-header">
        {t("Item Sales")}
      </h1>
      <div className="ui-customer-item-sales-summary-container">
        {itemSalesSummary ? (
          <div className="ui-customer-item-sales-summary">
            <span className="customer-stat">
              <p className="stat-title">{t('Orders')}</p>
              <p className="stat-number">{itemSalesSummary.order_count}</p>
            </span>
            <span className="customer-stat">
              <p className="stat-title">{t('Total Spent')}</p>
              <p className="stat-number">
                <LocaleCurrency amount={itemSalesSummary.total_spend} />
              </p>
            </span>
            {itemSalesSummary.order_count > 0 ? (
              <span className="customer-stat">
                <p className="stat-title">{t('AVG Spend Per Order')}</p>
                <p className="stat-number">
                  <LocaleCurrency amount={(itemSalesSummary.total_spend / itemSalesSummary.order_count).toFixed(2)} />
                </p>
              </span>
            ) : null}
          </div>
        ) : (
          <div style={{ height: "32px", marginBottom: "16px" }}>
            <Spin />
          </div>
        )}
      </div>
      <OrderFilters
        filters={filterState}
        setFilters={setFilterState}
        filterGroups={{
          users: state.facilityAdminFilters,
          registers: state.registerFilters,
          date: orderFilters.dateFilters,
          financial: orderFilters.financialStatusFilters,
          salesChannels: orderFilters.salesChannelFilters,
          creditCard: orderFilters.creditCardFilters,
          paymentOptions: state.paymentOptionFilters,
        }}
        historyKey="facility_customer_order_filters"
        dropFilterHistoryKeyPrefix="facility_customer_order_filters"
      />

      <div className="flex-grow mb-4 mt-4">
        <Search
          historyKey={"customer-item-sales-search"}
          searchCallback={searchValue => setFilters({ ...filters, offset: 0, search: searchValue })}
          placeholder={t("Search")}
        />
      </div>
      <Card>
        <Card.Section table="true">
          <OrdersTable
            orders={orders}
            tableLimit={filters.limit}
            tableOffset={filters.offset}
            handleTableOffset={direction =>
              setFilters(prev => ({
                ...prev,
                offset: direction === "prev" ? prev.offset - prev.limit : prev.offset + prev.limit,
              }))
            }
          />
        </Card.Section>
      </Card>
    </div>
  );
}
